import {
  deleteRevenueTypeData,
  toggleRevenueTypeStatus,
  getShowRevenueType,
} from "@/Api/revenueTypes";
import { ApiDataProps } from "@/pages/Tenants";
import { ExpenseTypeProps } from "@/types/expenseType";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TypePageActions } from "@/pages/Plans";
import { formatDate } from "@/utils/format";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { MoreHorizontal } from "lucide-react";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { DeleteBtn } from "@/components/Delete";
import { ShowBtn } from "@/components/Show";
import { UpdateExpenseTypeBtn } from "./Update";
import { CreateLoaderInput } from "@/components/Loaders";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const TableData = ({
  dataApi,
  updateFront,
  pageActions,
}: {
  dataApi: ApiDataProps<ExpenseTypeProps>;
  updateFront: (data: ApiDataProps<ExpenseTypeProps>) => void;
  pageActions: TypePageActions;
}) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Categoria de Receita</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((revenueType, index) => (
            <TableRow key={revenueType.id}>
              <TableCell className="truncate max-w-80">
                {revenueType.name}
              </TableCell>
              <TableCell>{revenueType?.category?.name || "—"}</TableCell>
              <TableCell>
                <ComponentSwitchToggleStatus
                  defaultChecked={Boolean(revenueType.is_active)}
                  params={{
                    func: toggleRevenueTypeStatus,
                    funcParams: revenueType.uuid,
                  }}
                  updateFront={() => {
                    const updatedData = {
                      ...dataApi,
                      data: dataApi.data.map((item) => {
                        if (item.uuid === revenueType.uuid)
                          return {
                            ...item,
                            is_active: item.is_active ? 0 : 1,
                          };
                        return item;
                      }),
                    };
                    updateFront(updatedData);
                  }}
                />
              </TableCell>
              <TableCell className="text-center">
                <div className="flex gap-3 w-full justify-center">
                  <If condition={hasPermission("edit-income-type")}>
                    <UpdateExpenseTypeBtn
                      uuid={revenueType.uuid}
                      updateFront={(response) => {
                        const updatedData = { ...dataApi };
                        updatedData.data[index] = response;
                        updateFront(updatedData);
                      }}
                    />
                  </If>
                  <If condition={hasPermission("delete-income-type")}>
                    <DeleteBtn
                      params={{
                        func: deleteRevenueTypeData,
                        funcParams: revenueType.uuid,
                      }}
                      updateFront={() => {
                        const updatedData = {
                          ...dataApi,
                          data: dataApi.data.filter(
                            (item) => item.uuid !== revenueType.uuid
                          ),
                        };
                        updateFront(updatedData);
                      }}
                      referenceLabel={revenueType.name}
                    />
                  </If>
                  <If condition={hasPermission("show-income-type-details")}>
                    <ShowBtn
                      labels={{ title: "Detalhes do Tipo de Receita" }}
                      params={{
                        func: getShowRevenueType,
                        funcParams: revenueType.uuid,
                      }}
                      render={(data: ExpenseTypeProps) => (
                        <div className="flex flex-col gap-3">
                          <div>
                            <h3 className="text-sm font-medium">Nome:</h3>
                            <p>{data.name}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium">Descrição:</h3>
                            <p>{data.description || "—"}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium">Categoria:</h3>
                            <p>{data?.category?.name || "—"}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium">Status:</h3>
                            <Badge
                              variant={
                                data.is_active ? "default" : "destructive"
                              }
                            >
                              {data.is_active ? "Ativo" : "Inativo"}
                            </Badge>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium">
                              Data de Criação:
                            </h3>
                            <p>{formatDate(data.created_at)}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium">
                              Última Atualização:
                            </h3>
                            <p>{formatDate(data.updated_at || "")}</p>
                          </div>
                        </div>
                      )}
                      loader={<CreateLoaderInput lines={6} />}
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={8}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
